// src/pages/ForgotPassword.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  HiArrowLeft,
  HiEye,
  HiEyeOff,
  HiLockClosed,
  HiPhone,
  HiShieldCheck,
} from "react-icons/hi";
import {
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
} from "../toolkitServices/userApi";

const getErrMsg = (err) =>
  err?.data?.message ||
  err?.data?.msg ||
  err?.error ||
  err?.message ||
  "Something went wrong";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [otpTimer, setOtpTimer] = useState(0);
  const [targetPhone, setTargetPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useForm();

  const [requestOtp, { isLoading: isSendingOtp }] = useRequestOtpMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  const [resetPassword, { isLoading: isResetting }] =
    useResetPasswordMutation();

  const isLoading = isSendingOtp || isVerifyingOtp || isResetting;
  const passwordValue = watch("password");

  useEffect(() => {
    if (otpTimer <= 0) return;

    const timer = setTimeout(() => {
      setOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [otpTimer]);

  const sendOtpToMobile = async () => {
    const isValid = await trigger("phone");

    if (!isValid) {
      toast.error("Please enter valid mobile number.");
      return;
    }

    const values = getValues();
    const phone = String(values.phone || "").trim();

    try {
      await requestOtp({ phone }).unwrap();

      setTargetPhone(phone);
      setStep(2);
      setOtpTimer(60);

      toast.success("OTP sent successfully on your mobile number.");
    } catch (err) {
      toast.error(getErrMsg(err));
      console.log(err);
    }
  };

  const handleResetPassword = async (formData) => {
    if (step === 1) {
      await sendOtpToMobile();
      return;
    }

    if (String(formData.phone || "").trim() !== targetPhone) {
      toast.error("Mobile number changed. Please send OTP again.");
      setStep(1);
      setTargetPhone("");
      setOtpTimer(0);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Password and confirm password do not match.");
      return;
    }

    try {
      await verifyOtp({
        phone: targetPhone,
        otp: formData.otp,
      }).unwrap();

      await resetPassword({
        phone: targetPhone,
        otp: formData.otp,
        password: formData.password,
        newPassword: formData.password,
      }).unwrap();

      toast.success("Password reset successfully. Please login.");
      navigate("/login");
    } catch (err) {
      toast.error(getErrMsg(err));
      console.log(err);
    }
  };

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-x-hidden bg-gradient-to-br from-[#f5f3ff] via-[#fefce8] to-[#e0f2fe] px-3 py-4 sm:px-4 md:h-dvh md:overflow-hidden md:py-3">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(196,181,253,0.28),transparent_55%),radial-gradient(circle_at_bottom,_rgba(251,191,36,0.18),transparent_55%)]" />
        <div className="absolute -top-24 left-6 h-56 w-56 rounded-full bg-[#c4b5fd]/40 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="flex w-full max-w-4xl overflow-hidden rounded-3xl bg-white/90 shadow-xl shadow-violet-200/70 ring-1 ring-violet-100 backdrop-blur-md md:h-[min(560px,calc(100dvh-24px))]"
      >
        <div className="flex w-full flex-col md:flex-row">
          <div className="flex w-full flex-col border-slate-100 px-5 py-5 sm:px-7 md:w-1/2 md:border-r md:px-8 md:py-5">
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mb-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-600 hover:text-violet-700"
              >
                <HiArrowLeft className="text-sm" />
                Back to login
              </button>

              <h2 className="text-lg font-semibold text-slate-900 sm:text-xl md:text-2xl">
                Reset your password
              </h2>

              <p className="mt-1.5 text-[11px] leading-5 text-slate-500 sm:text-xs">
                Enter your registered mobile number. We will send an OTP to
                verify your account.
              </p>
            </div>

            <form
              onSubmit={handleSubmit(handleResetPassword)}
              className="mt-3 flex min-h-0 flex-1 flex-col"
            >
              <div className="space-y-2.5 md:space-y-2">
                {/* Mobile Number */}
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Registered Mobile Number
                  </label>

                  <div
                    className={`group mt-1 flex h-[42px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.phone ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiPhone className="text-sm" />
                      </span>
                    </div>

                    <div className="flex h-full items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-violet-700">
                      +91
                    </div>

                    <input
                      {...register("phone", {
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Enter a valid 10 digit mobile number",
                        },
                      })}
                      disabled={step === 2}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="9876543210"
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                      }}
                    />
                  </div>

                  {errors.phone && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                {step === 2 && (
                  <>
                    {/* OTP */}
                    <div>
                      <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                        Enter OTP
                      </label>

                      <div
                        className={`group mt-1 flex h-[42px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                          errors.otp ? "border-red-400" : "border-slate-200"
                        }`}
                      >
                        <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                            <HiShieldCheck className="text-sm" />
                          </span>
                        </div>

                        <input
                          {...register("otp", {
                            required: "OTP is required",
                            pattern: {
                              value: /^[0-9]{6}$/,
                              message: "Enter a valid 6 digit OTP",
                            },
                          })}
                          className="h-full w-full bg-transparent px-3 text-sm font-semibold tracking-[0.35em] text-slate-900 outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400"
                          type="tel"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter OTP"
                          onInput={(e) => {
                            e.target.value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6);
                          }}
                        />
                      </div>

                      {errors.otp && (
                        <p className="mt-1 text-[11px] text-red-500">
                          {errors.otp.message}
                        </p>
                      )}

                      <div className="mt-1.5 flex items-center justify-between gap-3 rounded-xl bg-violet-50 px-3 py-1.5">
                        <p className="text-[10px] font-medium text-slate-600">
                          OTP sent to{" "}
                          <span className="font-semibold text-violet-700">
                            +91 {targetPhone}
                          </span>
                        </p>

                        <button
                          type="button"
                          onClick={sendOtpToMobile}
                          disabled={isSendingOtp || otpTimer > 0}
                          className="shrink-0 text-[10px] font-semibold text-violet-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                        >
                          {isSendingOtp
                            ? "Sending..."
                            : otpTimer > 0
                            ? `Resend in ${otpTimer}s`
                            : "Resend OTP"}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                        New Password
                      </label>

                      <div
                        className={`group relative mt-1 flex h-[42px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                          errors.password
                            ? "border-red-400"
                            : "border-slate-200"
                        }`}
                      >
                        <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                            <HiLockClosed className="text-sm" />
                          </span>
                        </div>

                        <input
                          {...register("password", {
                            required: "New password is required",
                            minLength: {
                              value: 6,
                              message: "Minimum 6 characters required",
                            },
                          })}
                          className="h-full w-full bg-transparent px-3 pr-11 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create new password"
                        />

                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-violet-50 hover:text-violet-600"
                        >
                          {showPassword ? <HiEyeOff /> : <HiEye />}
                        </button>
                      </div>

                      {errors.password && (
                        <p className="mt-1 text-[11px] text-red-500">
                          {errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                        Confirm Password
                      </label>

                      <div
                        className={`group relative mt-1 flex h-[42px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                          errors.confirmPassword
                            ? "border-red-400"
                            : "border-slate-200"
                        }`}
                      >
                        <div className="flex h-full min-w-[50px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                            <HiLockClosed className="text-sm" />
                          </span>
                        </div>

                        <input
                          {...register("confirmPassword", {
                            required: "Confirm password is required",
                            validate: (value) =>
                              value === passwordValue ||
                              "Password and confirm password do not match",
                          })}
                          className="h-full w-full bg-transparent px-3 pr-11 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-violet-50 hover:text-violet-600"
                        >
                          {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                        </button>
                      </div>

                      {errors.confirmPassword && (
                        <p className="mt-1 text-[11px] text-red-500">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="mt-3 shrink-0">
                <button
                  disabled={isLoading}
                  type="submit"
                  className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-400/60 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-80"
                >
                  {isSendingOtp
                    ? "Sending OTP..."
                    : isVerifyingOtp
                    ? "Verifying OTP..."
                    : isResetting
                    ? "Resetting password..."
                    : step === 1
                    ? "Send OTP"
                    : "Reset Password"}
                </button>

                <p className="mt-3 text-center text-[11px] text-slate-500">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="font-medium text-violet-600 hover:underline"
                  >
                    Log in
                  </Link>
                </p>
              </div>
            </form>
          </div>

          <div className="relative hidden bg-gradient-to-br from-[#312e81] via-[#4c1d95] to-[#f97316] text-white md:flex md:w-1/2">
            <div className="flex flex-1 items-center justify-center px-6 py-6">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-6 rounded-[32px] bg-white/15 blur-3xl" />

                <div className="relative flex items-center justify-center overflow-hidden rounded-[28px] bg-slate-900/30 shadow-2xl shadow-black/40 ring-1 ring-white/25 backdrop-blur-sm">
                  <img
                    src="/welcome.svg"
                    alt="Reset password"
                    className="h-60 w-full object-contain p-5 lg:h-64"
                  />
                </div>
              </div>
            </div>

            <p className="absolute bottom-5 right-6 text-[10px] text-white/75">
              Secure OTP verification · Password recovery
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;